import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  VStack,
  HStack,
  Icon,
  useToast,
  Box,
  Divider,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import { MF_ACCESS_REVIEWS } from "state/members/accessReviews";
import { MF_OPTIONS } from "state/members/options";
import FilterDropdown from "components/FilterDropdown";
import { Option } from "state/options";
import { FiUsers, FiUserPlus, FiX, FiTag, FiCheck, FiGrid } from "react-icons/fi";

interface AddAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  reviewId: string;
  resourceId: string;
  onSuccess?: () => void;
  /** The review takes no more changes (its cycle ended): confirm is off. */
  isLocked?: boolean;
  /**
   * A combined review's resources: the reviewer picks which one the missing
   * account is on. Absent on a per-resource review, whose add is on its own.
   */
  resourceOptions?: Option[];
}

const AddAccountModal: React.FC<AddAccountModalProps> = ({
  isOpen,
  onClose,
  reviewId,
  resourceId,
  onSuccess,
  isLocked = false,
  resourceOptions,
}) => {
  const [selectedAccount, setSelectedAccount] = useState<Option[]>([]);
  const [selectedResource, setSelectedResource] = useState<Option[]>([]);
  const combined = !!resourceOptions;
  const chosenResourceId = combined ? selectedResource[0]?.value : undefined;
  const [selectedEntitlements, setSelectedEntitlements] = useState<Option[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // Get the options for selection
  const options = MF_OPTIONS.useListOptions();

  // Setup the mutation
  const { mutate, isLoading } =
    MF_ACCESS_REVIEWS.useAddAccountToCampaignReview();

  // Get member options
  const memberOptions: Option[] = options?.members || [];

  // Get entitlement options for this review's resource
  const { data: entitlementOptions } =
    MF_ACCESS_REVIEWS.useGetReviewEntitlements(
      combined && !chosenResourceId ? null : reviewId,
      chosenResourceId,
    );

  const entitlementDropdownOptions: Option[] =
    entitlementOptions?.map((e) => ({
      value: e.id,
      label: e.name,
    })) || [];

  const handleSubmit = async () => {
    if (isLocked) return;
    if (selectedAccount.length === 0) {
      toast({
        title: "Please select an account",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (combined && !chosenResourceId) {
      toast({
        title: "Please select the resource the account is on",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await mutate({
        reviewId,
        accountId: selectedAccount[0].value,
        resourceId: chosenResourceId,
        entitlementIds:
          selectedEntitlements.length > 0
            ? selectedEntitlements.map((e) => e.value)
            : [],
      });

      // Show success message
      toast({
        title: "Account added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Clear form and close modal
      setSelectedAccount([]);
      setSelectedResource([]);
      setSelectedEntitlements([]);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error adding account:", error);

      // Show error message
      toast({
        title: "Failed to add account",
        description:
          typeof error === "string"
            ? error
            : "An error occurred while adding the account",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset selections when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedAccount([]);
      setSelectedResource([]);
      setSelectedEntitlements([]);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="12px" boxShadow="lg">
        <ModalHeader
          fontSize="md"
          fontWeight="500"
          color="gray.800"
          pb={2}
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          <HStack>
            <Icon as={FiUserPlus} boxSize="16px" color="gray.500" />
            <Box>Add Account to Review</Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton size="sm" top="12px" right="12px" />

        <ModalBody py={4} px={4}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                Employee
              </FormLabel>
              <FilterDropdown
                initialOptions={memberOptions}
                value={selectedAccount}
                onChange={setSelectedAccount}
                placeholder="Select employee"
                icon={FiUsers}
                searchable={true}
                singleSelect={true}
              />
              <FormHelperText fontSize="xs" mt={1}>
                Select an employee that needs to be reviewed
              </FormHelperText>
            </FormControl>

            {combined && (
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                  Resource
                </FormLabel>
                <FilterDropdown
                  initialOptions={resourceOptions}
                  value={selectedResource}
                  onChange={(next: Option[]) => {
                    setSelectedResource(next);
                    setSelectedEntitlements([]);
                  }}
                  placeholder="Select resource"
                  icon={FiGrid}
                  searchable={true}
                  singleSelect={true}
                />
                <FormHelperText fontSize="xs" mt={1}>
                  The resource in this review the account is on
                </FormHelperText>
              </FormControl>
            )}

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                Entitlements
              </FormLabel>
              {/* Remounted per resource: the dropdown keeps options it has
                  seen, so resource A's entitlements would stay pickable for B. */}
              <FilterDropdown
                key={chosenResourceId ?? "review"}
                initialOptions={entitlementDropdownOptions}
                value={selectedEntitlements}
                onChange={setSelectedEntitlements}
                placeholder="Select entitlements"
                icon={FiTag}
                searchable={true}
                singleSelect={false}
              />
              <FormHelperText fontSize="xs" mt={1}>
                Optionally assign entitlements for this account
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>

        <Divider borderColor="gray.100" />

        <ModalFooter pt={3} pb={3}>
          <HStack spacing={2}>
            <Button
              variant="subtle"
              tone="neutral"
              size="md"
              onClick={onClose}
              isDisabled={isSubmitting}
              leadingIcon={<Icon as={FiX} boxSize="14px" />}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              tone="primary"
              size="md"
              onClick={handleSubmit}
              isLoading={isLoading || isSubmitting}
              isDisabled={isLocked}
              leadingIcon={<Icon as={FiCheck} boxSize="14px" />}
            >
              Add Account
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddAccountModal;
